import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { IConnectorData, IConnectorProps, DeltaOf } from '@tencent/slide-sdk-model';
import { ElementCommonPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/element-common-property-converter';
import { GraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/graphic-info/graphic-info-converter';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { ConnectorInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/connector-info/connector-info-converter';
import { BorderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/border/border-converter';
import { LockingConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/locking/locking-converter';
import { Locks } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';

/**
 * 连接线
 */
export class ConnectorPropertyConverter {
    /**
     * 连接线 sdk -> ui 属性映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IConnectorProps>>,
        uiData: IConnectorModel,
    ): void {
        // **** 公共属性 ****

        // frame
        ElementCommonPropertyConverter.convertPartialDataFromSdkToUi(sdkData, uiData);
        // 形状绘图属性
        if (sdkData.spPr?.prstGeom) {
            GraphicInfoConverter.convertGraphicLineFromSdkToUi(sdkData.spPr.prstGeom, uiData.props.style);
        }
        // 边框
        if (sdkData.spPr?.ln) {
            uiData.props.style.border = uiData.props.style.border || {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.spPr.ln, uiData.props.style.border);
        } else if (uiData.props.style.border) {
            delete uiData.props.style.border;
        }
        // 锁定纵横比
        if (sdkData.nvPr) {
            LockingConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.locking, uiData.props);
        }

        // **** 独有属性 ****

        // 连接线独有属性信息（1、两端连接的图形）
        ConnectorInfoConverter.convertPartialDataFromSdkToUi(sdkData, uiData);
    }

    /**
     * 连接线 ui -> sdk 属性映射
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    // eslint-disable-next-line complexity
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IConnectorModel>,
        sdkData: DeltaOf<IConnectorData>,
        rawUiData: IConnectorModel,
    ): void {
        sdkData.spPr = sdkData.spPr || {};
        sdkData.nvPr = sdkData.nvPr || {};

        // **** 公共属性 ****

        // frame
        ElementCommonPropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, rawUiData);
        // 图形信息转换
        const style = uiData.props?.style;
        if (style?.bgLine || style?.controlValues) {
            sdkData.spPr.prstGeom = GraphicInfoConverter.convertPartialDataFromUiToSdk({
                // @ts-ignore
                bgShape: style.bgLine ?? rawUiData.props?.style?.bgLine,
                controlValues: style.controlValues,
            });
        }
        // 边框
        if (uiData.props?.style?.border) {
            sdkData.spPr.ln = sdkData.spPr.ln || {};
            BorderConverter.convertPartialDataFromUiToSdk(uiData.props.style.border, sdkData.spPr.ln);
        } else if (uiData.props?.style?.border === null) {
            sdkData.spPr.ln = null;
        }
        // 锁定纵横比
        if (uiData.props?.[Locks.LOCKS]) {
            sdkData.nvPr.locking = sdkData.nvPr.locking || {};
            LockingConverter.convertPartialDataFromUiToSdk(uiData.props, sdkData.nvPr.locking);
        }

        // **** 独有属性 ****

        // 连接线独有属性信息（1、两端连接的图形）
        ConnectorInfoConverter.convertPartialDataFromUiToSdk(uiData, sdkData);
    }
}
